import { ConnectionPositionPair } from '@angular/cdk/overlay';
import { Component, effect, inject, input } from '@angular/core';
import { WidgetService } from '../../../services/builder/widget.service';
import { TextBoxComponent } from '../TextBox/textbox-edit';
import { NumericUpDownComponent } from '../NumericUpDown/numericupdown-edit';
import { DatePickerComponent } from '../DatePicker/datepicker-edit';
import { ComboBoxComponent } from '../ComboBox/combobox-edit';
import { RadioButtonComponent } from '../RadioButton/radiobutton-edit';
import { MultiRadioButtonComponent } from '../MultiRadioButton/multi-radiobutton-edit';
import { MultiplierRadioButtonComponent } from '../MultiplierRadioButton/multiplier-radiobutton-edit';
import { StaticTextComponent } from '../StaticText/staticitext-edit';
import { CheckBoxComponent } from '../CheckBox/checkbox-edit';
import { FileDialogComponent } from '../FileDialog/filedialog-edit';
import { MatIcon } from '@angular/material/icon';
import { CdkMenuModule } from '@angular/cdk/menu';
import { RulesService } from '../../../services/builder/rules.service';
import { NgClass } from '@angular/common';
import {
  CdkDragPlaceholder,
  CdkDragPreview,
  CdkDrag,
} from '@angular/cdk/drag-drop';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'lib-base-widget-edit',
  template: `
    <!-- CONTEXT MENU -->
    <ng-template #menu>
      <div
        class="mr-2 flex flex-row items-center rounded-full bg-slate-200 p-1 shadow-md"
        cdkMenu
      >
        <button
          (click)="this.widgetService.settingVisibility.set(true)"
          pTooltip="Personalizza Elemento"
          tooltipPosition="bottom"
          mat-icon-button
          cdkMenuItem
        >
          <mat-icon svgIcon="heroicons_solid:cog-8-tooth"></mat-icon>
        </button>
        <button
          (click)="widgetService.removeWidget()"
          pTooltip="Rimuovi Elemento"
          tooltipPosition="bottom"
          mat-icon-button
          cdkMenuItem
        >
          <mat-icon svgIcon="heroicons_solid:trash"></mat-icon>
        </button>
        <button
          pTooltip="Copia Elemento"
          tooltipPosition="bottom"
          mat-icon-button
          cdkMenuItem
          (click)="widgetService.copyWidget()"
        >
          <mat-icon svgIcon="heroicons_solid:clipboard"></mat-icon>
        </button>
        @if (widgetService.pasteEnabled()) {
          <button
            (click)="widgetService.pasteWidget()"
            pTooltip="Incolla Elemento"
            cdkMenuItem
            mat-icon-button
          >
            <mat-icon svgIcon="heroicons_solid:clipboard-document"></mat-icon>
          </button>
        }
      </div>
    </ng-template>

    @let hasError = widgetService.widgetErrors().missingIds.length > 0;
    <div
      [id]="widgetId()"
      cdkDrag
      [cdkDragData]="widgetId()"
      (click)="setWidget()"
      [ngClass]="[
        rulesService.selectionActive() ? 'cursor-pointer' : '',
        hasError ? 'bg-[#f2e2e2]' : 'bg-[#f2f3f5]',
      ]"
      class="flex flex-col gap-2 rounded-md px-2 pb-[0.5rem] pt-4"
    >
      <div
        class="flex items-center gap-4"
        [ngClass]="
          widgetService.baseElementShowDescription() ||
          rulesService.selectionActive()
            ? 'justify-between'
            : 'justify-end'
        "
      >
        <div class="flex flex-col gap-2">
          @if (rulesService.selectionActive()) {
            <span class="font-semibold">{{ '$' + widgetId() + '$' }}</span>
          }
          @if (widgetService.baseElementShowDescription()) {
            <span>
              {{ widgetService.computedDescription() }}
            </span>
          }
        </div>
        <div class="flex min-w-44 items-center justify-end gap-2">
          @if (hasError) {
            <mat-icon
              svgIcon="heroicons_solid:exclamation-triangle"
              class="text-amber-700"
            ></mat-icon>
          }
          <mat-icon
            [cdkMenuTriggerFor]="menu"
            [cdkMenuPosition]="positions"
            svgIcon="heroicons_solid:ellipsis-vertical"
            class="rounded-full hover:bg-black/10"
          ></mat-icon>
        </div>
      </div>
      @switch (widgetService.widget()?._mappedType) {
        @case ('TextBox') {
          <lib-textbox-edit></lib-textbox-edit>
        }
        @case ('NumericUpDown') {
          <lib-numericupdown-edit></lib-numericupdown-edit>
        }
        @case ('DatePicker') {
          <lib-datepicker-edit></lib-datepicker-edit>
        }
        @case ('ComboBox') {
          <lib-combobox-edit></lib-combobox-edit>
        }
        @case ('CheckBox') {
          <lib-checkbox-edit></lib-checkbox-edit>
        }
        @case ('RadioButton') {
          <lib-radiobutton-edit></lib-radiobutton-edit>
        }
        @case ('MultiRadioButton') {
          <lib-multi-radiobutton-edit></lib-multi-radiobutton-edit>
        }
        @case ('MultiplierRadioButton') {
          <lib-multiplier-radiobutton-edit></lib-multiplier-radiobutton-edit>
        }
        @case ('FileDialog') {
          <lib-filedialog-edit></lib-filedialog-edit>
        }
        @case ('StaticText') {
          <lib-statictext-edit></lib-statictext-edit>
        }
      }

      <div
        *cdkDragPreview
        class="flex cursor-pointer flex-row rounded-2xl bg-white py-3 pl-2 pr-3 shadow-md"
      >
        <div class="w-max justify-center self-center px-2">
          {{ widgetId() }}
        </div>
      </div>

      <div *cdkDragPlaceholder class="placeholder flex-1 p-3">
        <div
          class="min-h-10 w-full rounded-md bg-gray-500 bg-opacity-15 outline-dotted outline-4 outline-offset-2 outline-gray-300"
        ></div>
      </div>
    </div>
  `,
  imports: [
    NgClass,
    MatIcon,
    MatButtonModule,
    CdkMenuModule,
    TextBoxComponent,
    NumericUpDownComponent,
    DatePickerComponent,
    ComboBoxComponent,
    RadioButtonComponent,
    MultiRadioButtonComponent,
    MultiplierRadioButtonComponent,
    StaticTextComponent,
    CheckBoxComponent,
    FileDialogComponent,
    CdkDragPreview,
    CdkDragPlaceholder,
    CdkDrag,
  ],
  providers: [WidgetService],
})
export class BaseWidgetEditComponent {
  protected widgetService = inject(WidgetService);
  protected rulesService = inject(RulesService);

  widgetId = input.required<string>();

  positions = [
    new ConnectionPositionPair(
      { originX: 'start', originY: 'center' },
      { overlayX: 'end', overlayY: 'center' },
    ),
  ];

  constructor() {
    effect(() => (this.widgetService.uniqueId = this.widgetId()));
  }

  setWidget() {
    if (!this.rulesService.selectionActive()) return;
    this.rulesService.clickedWidget.set(this.widgetId());
  }
}
